// ═══════════════════════════════════════════════════════════════════════════════
// ElementCatalogPage — Patient Briefing Elements catalog (SME-friendly)
// ═══════════════════════════════════════════════════════════════════════════════
//
// PURPOSE
//   Browse and edit the catalog of patient-state elements that compose into
//   briefings. Designed for clinical SMEs first; engineer-friendly signals
//   (RendererClass, SortOrder, renderer-drift warnings) are hidden behind an
//   "Advanced view" toggle so the default surface is layman-friendly.
//
// DEFAULT (SME) VIEW
//   Cards grouped by Category. Each card communicates:
//     - Display Name + plain-language description
//     - Sample output (the literal text the LLM will see at runtime —
//       authored in sampleOutputs.ts; SME picks "Preview" to see the
//       sample in context)
//     - Refresh cadence pill ("Stable" / "Per-turn") with tooltip
//     - Active toggle (the row-level kill switch from memory
//       feedback_kill_switch_via_row_isactive)
//
// ADVANCED VIEW
//   Same table as before: Code (mono), Renderer class, Tier (T1/T2), Tokens,
//   SortOrder, drift signals, full filter bar. For engineers/admins.
//
// KILL-SWITCH
//   The Active switch flips ai.BriefingContextElement.IsActive — surgical
//   "renderer broken, disable everywhere" gesture.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  EditOutlined,
  ExperimentOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { colors, typography } from '@/theme/tokens';
import {
  addElement,
  listElements,
  listRendererClassNames,
  previewBriefingElement,
  searchMembersForPreview,
  setElementActive,
  updateElement,
  type BriefingContextElement,
} from '@/api/briefingContext';
import { getSampleOutput } from './sampleOutputs';
import { ElementPreviewDrawer } from '@shared/briefing-preview';
import { sessionHelper } from '@/auth/sessionHelper';

const { Text, Title, Paragraph } = Typography;

// Pre-seeded categories so the editor Select doesn't depend on what's in the
// DB; SMEs can still author custom values (the column is a free string).
const KNOWN_CATEGORIES = [
  'Identity',
  'Clinical Baseline',
  'Treatment State',
  'Symptoms',
  'Specialty',
  'Care Coordination',
];

// Persistent UI mode. Cards is the SME default; Table is the engineer mode.
type ViewMode = 'cards' | 'table';
const VIEW_MODE_KEY = 'agentstudio.briefing-elements.viewMode';

// Filter persistence (Phase 9, 2026-05-22). Each filter is its own key so a
// future bump that adds a fourth filter doesn't have to migrate a JSON blob.
// "(none)" sentinel distinguishes "explicitly cleared" from "never set" so a
// user who cleared a filter doesn't have it auto-restored on next load.
const FILTER_CATEGORY_KEY = 'agentstudio.briefing-elements.filter.category';
const FILTER_TIER_KEY = 'agentstudio.briefing-elements.filter.tier';
const FILTER_DRIFT_KEY = 'agentstudio.briefing-elements.filter.driftOnly';
const FILTER_NONE = '(none)';

function readPersistedString(key: string): string | undefined {
  try {
    const v = window.localStorage.getItem(key);
    if (v == null || v === FILTER_NONE) return undefined;
    return v;
  } catch {
    return undefined;
  }
}

function readPersistedTier(): number | undefined {
  const raw = readPersistedString(FILTER_TIER_KEY);
  if (raw == null) return undefined;
  const n = Number(raw);
  return n === 1 || n === 2 ? n : undefined;
}

function readPersistedBool(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function writePersisted(key: string, value: string | number | boolean | undefined | null): void {
  try {
    if (value == null || value === '' || value === false) {
      window.localStorage.setItem(key, FILTER_NONE);
    } else {
      window.localStorage.setItem(key, String(value));
    }
  } catch {
    /* localStorage full / disabled — ignore */
  }
}

// Plain-language tier copy. Used in both views.
const TIER_COPY: Record<number, { label: string; tooltip: string; color: string }> = {
  1: {
    label: 'Stable',
    tooltip:
      'Cached during the conversation. Rendered once on the first turn and reused for follow-ups — saves tokens.',
    color: 'blue',
  },
  2: {
    label: 'Per-turn',
    tooltip:
      'Refreshed every message. Used for state that changes minute-to-minute (recent vitals, active symptoms).',
    color: 'purple',
  },
};

export function ElementCatalogPage() {
  const [rows, setRows] = useState<BriefingContextElement[]>([]);
  const [rendererClasses, setRendererClasses] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [search, setSearch] = useState('');

  // SME / engineer mode. Persisted across sessions.
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const v = window.localStorage.getItem(VIEW_MODE_KEY);
      return v === 'table' ? 'table' : 'cards';
    } catch {
      return 'cards';
    }
  });

  function setViewModePersisted(v: ViewMode): void {
    setViewMode(v);
    try {
      window.localStorage.setItem(VIEW_MODE_KEY, v);
    } catch {
      /* localStorage full / disabled — ignore */
    }
  }

  // Filter state — shared across both views. Persisted to localStorage so
  // a user who's been refining a view doesn't lose it on reload (Phase 9).
  // The lazy initializer keeps the state initial value out of the render
  // closure so we read localStorage exactly once on mount, not on every
  // re-render.
  const [categoryFilter, setCategoryFilterRaw] = useState<string | undefined>(() =>
    readPersistedString(FILTER_CATEGORY_KEY),
  );
  const [tierFilter, setTierFilterRaw] = useState<number | undefined>(() => readPersistedTier());
  const [driftOnly, setDriftOnlyRaw] = useState<boolean>(() => readPersistedBool(FILTER_DRIFT_KEY));

  function setCategoryFilter(v: string | undefined): void {
    setCategoryFilterRaw(v);
    writePersisted(FILTER_CATEGORY_KEY, v);
  }
  function setTierFilter(v: number | undefined): void {
    setTierFilterRaw(v);
    writePersisted(FILTER_TIER_KEY, v);
  }
  function setDriftOnly(v: boolean): void {
    setDriftOnlyRaw(v);
    writePersisted(FILTER_DRIFT_KEY, v);
  }

  // Editor + preview state
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState<BriefingContextElement | null>(null);
  const [previewRow, setPreviewRow] = useState<BriefingContextElement | null>(null);

  async function loadAll() {
    setLoading(true);
    try {
      const [elements, renderers] = await Promise.all([
        listElements(includeInactive),
        listRendererClassNames(),
      ]);
      setRows(elements);
      setRendererClasses(renderers);
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Failed to load element catalog');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  // ── Derived state ─────────────────────────────────────────────────────────
  const rendererClassSet = useMemo(() => new Set(rendererClasses), [rendererClasses]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && ![r.code, r.displayName, r.description, r.rendererClass]
        .filter(Boolean)
        .some((s) => s!.toLowerCase().includes(q))) return false;
      if (categoryFilter && r.category !== categoryFilter) return false;
      if (tierFilter != null && r.tier !== tierFilter) return false;
      if (driftOnly && !(r.rendererClass && !rendererClassSet.has(r.rendererClass))) return false;
      return true;
    });
  }, [rows, search, categoryFilter, tierFilter, driftOnly, rendererClassSet]);

  // Group cards by Category, keeping KNOWN_CATEGORIES ordering and putting
  // "(Uncategorized)" / custom values at the end alphabetically.
  const groupedCards = useMemo(() => {
    if (viewMode !== 'cards') return [] as Array<[string, BriefingContextElement[]]>;
    const buckets = new Map<string, BriefingContextElement[]>();
    for (const r of visible) {
      const cat = r.category ?? '(Uncategorized)';
      const arr = buckets.get(cat) ?? [];
      arr.push(r);
      buckets.set(cat, arr);
    }
    // Order: KNOWN_CATEGORIES first (in order), then any extras alphabetically,
    // with "(Uncategorized)" last.
    const known = KNOWN_CATEGORIES.filter((c) => buckets.has(c));
    const extras = Array.from(buckets.keys())
      .filter((c) => !KNOWN_CATEGORIES.includes(c) && c !== '(Uncategorized)')
      .sort();
    const ordered = [...known, ...extras];
    if (buckets.has('(Uncategorized)')) ordered.push('(Uncategorized)');
    return ordered.map((cat) => [cat, buckets.get(cat) ?? []] as const) as Array<
      [string, BriefingContextElement[]]
    >;
  }, [visible, viewMode]);

  const categoryOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const r of rows) {
      if (r.category) seen.add(r.category);
    }
    const known = KNOWN_CATEGORIES.filter((c) => seen.has(c));
    const extra = Array.from(seen).filter((c) => !KNOWN_CATEGORIES.includes(c)).sort();
    return [...known, ...extra];
  }, [rows]);

  const stats = useMemo(() => {
    const total = visible.length;
    const inactive = visible.filter((r) => !r.isActive).length;
    const drift = visible.filter(
      (r) => r.rendererClass && !rendererClassSet.has(r.rendererClass),
    ).length;
    return { total, inactive, drift };
  }, [visible, rendererClassSet]);

  const hasActiveFilter = Boolean(
    search.trim() || categoryFilter || tierFilter != null || driftOnly,
  );

  function clearFilters() {
    setSearch('');
    setCategoryFilter(undefined);
    setTierFilter(undefined);
    setDriftOnly(false);
  }

  // ── Actions ──────────────────────────────────────────────────────────────
  function openCreate() {
    setEditRow({
      id: 0,
      code: '',
      displayName: '',
      description: '',
      category: KNOWN_CATEGORIES[0],
      rendererClass: '',
      tier: 1,
      defaultMaxTokens: 200,
      sortOrder: (rows.length + 1) * 10,
      isActive: true,
    });
    setEditOpen(true);
  }

  function openEdit(row: BriefingContextElement) {
    setEditRow({ ...row });
    setEditOpen(true);
  }

  async function handleToggleActive(row: BriefingContextElement, next: boolean) {
    try {
      await setElementActive(row.id, next);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, isActive: next } : r)));
      message.success(next ? `Enabled '${row.code}'` : `Disabled '${row.code}'`);
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Failed to update');
    }
  }

  async function handleSave(model: BriefingContextElement) {
    try {
      const saved = model.id > 0 ? await updateElement(model) : await addElement(model);
      setRows((prev) => {
        const idx = prev.findIndex((r) => r.id === saved.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = saved;
          return next;
        }
        return [...prev, saved];
      });
      message.success(model.id > 0 ? `Updated '${saved.code}'` : `Created '${saved.code}'`);
      setEditOpen(false);
      setEditRow(null);
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Save failed');
    }
  }

  // ── Table columns (Advanced view) ─────────────────────────────────────────
  const columns: ColumnsType<BriefingContextElement> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 280,
      sorter: (a, b) => a.code.localeCompare(b.code),
      render: (code: string, row) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontFamily: typography.fontMono, fontWeight: 600, fontSize: 12.5 }}>
            {code}
          </Text>
          {row.displayName && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.displayName}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 160,
      filters: KNOWN_CATEGORIES.map((c) => ({ text: c, value: c })),
      onFilter: (v, r) => r.category === v,
      render: (cat?: string) =>
        cat ? <StatusPill tone="slate">{cat}</StatusPill> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Renderer',
      dataIndex: 'rendererClass',
      key: 'rendererClass',
      width: 240,
      render: (cls?: string) => {
        if (!cls) return <Text type="secondary">—</Text>;
        const drift = !rendererClassSet.has(cls);
        return (
          <Space size={4}>
            <Text style={{ fontFamily: typography.fontMono, fontSize: 12 }}>{cls}</Text>
            {drift && (
              <Tooltip title="This class is not registered in DI. Likely renamed or removed in code without updating the SQL seed.">
                <Tag icon={<WarningOutlined />} color="warning" style={{ marginLeft: 0 }}>
                  drift
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Refresh',
      dataIndex: 'tier',
      key: 'tier',
      width: 110,
      align: 'center',
      render: (t: number) => {
        const meta = TIER_COPY[t] ?? { label: `T${t}`, tooltip: '', color: 'default' };
        return (
          <Tooltip title={meta.tooltip}>
            <Tag color={meta.color} style={{ marginRight: 0 }}>
              {meta.label}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Tokens',
      dataIndex: 'defaultMaxTokens',
      key: 'defaultMaxTokens',
      width: 80,
      align: 'right',
      render: (n: number) => (
        <Text style={{ fontFamily: typography.fontMono, fontSize: 12 }}>{n}</Text>
      ),
    },
    {
      title: 'Sort',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 70,
      align: 'right',
      sorter: (a, b) => a.sortOrder - b.sortOrder,
      defaultSortOrder: 'ascend',
      render: (n: number) => (
        <Text type="secondary" style={{ fontFamily: typography.fontMono, fontSize: 12 }}>
          {n}
        </Text>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      align: 'center',
      render: (v: boolean, row) => (
        <Tooltip title={v ? 'Kill switch: turn off to skip everywhere' : 'Disabled — runtime skips this element'}>
          <Switch
            size="small"
            checked={v}
            onChange={(next) => void handleToggleActive(row, next)}
          />
        </Tooltip>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      align: 'right',
      render: (_v, row) => (
        <Space size={2}>
          <Tooltip title="Preview sample output">
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => setPreviewRow(row)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="violet"
        icon={<ExperimentOutlined />}
        title={
          <Space size={6} align="center">
            <span>Briefing Elements</span>
            <HelpHotspot topic="briefing-context.elements" />
          </Space>
        }
        subtitle="The building blocks of patient briefings — each element gives the agent one piece of patient state every turn."
        count={rows.length}
        countLabel={rows.length === 1 ? 'element' : 'elements'}
      />

      {/* ── Filter bar + view switcher ───────────────────────────────────── */}
      <SectionCard noPadding>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 10,
            padding: '12px 16px',
          }}
        >
          <Input.Search
            placeholder="Search by name, description, or code…"
            allowClear
            style={{ width: 260 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <Select
            allowClear
            placeholder="Any category"
            style={{ width: 180 }}
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v)}
            options={categoryOptions.map((c) => ({ value: c, label: c }))}
          />

          <Select
            allowClear
            placeholder="Any refresh cadence"
            style={{ width: 180 }}
            value={tierFilter}
            onChange={(v) => setTierFilter(v)}
            options={[
              { value: 1, label: 'Stable (cached per turn)' },
              { value: 2, label: 'Per-turn (refreshed)' },
            ]}
          />

          <Tooltip title="Include rows turned off via the kill switch">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <Switch size="small" checked={includeInactive} onChange={setIncludeInactive} />
              Show inactive
            </span>
          </Tooltip>

          {/* Engineer-only filters live in Advanced mode */}
          {viewMode === 'table' && (
            <Tooltip title="Show only rows whose Renderer class isn't in the live registry">
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  color: driftOnly ? colors.amber[700] : undefined,
                }}
              >
                <Switch size="small" checked={driftOnly} onChange={setDriftOnly} />
                Drift only
              </span>
            </Tooltip>
          )}

          {hasActiveFilter && (
            <Button type="link" size="small" onClick={clearFilters} style={{ paddingInline: 4 }}>
              Clear filters
            </Button>
          )}

          <span style={{ flex: 1 }} />

          <Space size={6}>
            <Tag color="default">{stats.total} shown</Tag>
            {stats.inactive > 0 && <Tag color="default">{stats.inactive} inactive</Tag>}
            {viewMode === 'table' && stats.drift > 0 && (
              <Tag icon={<WarningOutlined />} color="warning">
                {stats.drift} renderer drift
              </Tag>
            )}
          </Space>

          <Segmented<ViewMode>
            size="middle"
            value={viewMode}
            onChange={(v) => setViewModePersisted(v as ViewMode)}
            options={[
              { value: 'cards', label: 'Cards', icon: <ExperimentOutlined /> },
              {
                value: 'table',
                label: (
                  <Tooltip title="Advanced view — shows code, renderer class, sort order, and drift signals for engineers">
                    <span>Advanced</span>
                  </Tooltip>
                ),
                icon: <InfoCircleOutlined />,
              },
            ]}
          />

          <Button icon={<ReloadOutlined />} onClick={() => void loadAll()} loading={loading}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New element
          </Button>
        </div>
      </SectionCard>

      {/* Drift warning only in Advanced view */}
      {viewMode === 'table' && stats.drift > 0 && (
        <Alert
          type="warning"
          showIcon
          message="Renderer drift detected"
          description={
            <>
              {stats.drift} row(s) reference a <code>RendererClass</code> that isn't registered in
              the current process's DI. Either the class was renamed/removed in code and the SQL
              seed wasn't updated, or the row was authored against a typo. Use Edit to repair —
              the dropdown lists the live registry.
            </>
          }
        />
      )}

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      {viewMode === 'cards' ? (
        <CardsView
          grouped={groupedCards}
          loading={loading}
          hasActiveFilter={hasActiveFilter}
          onEdit={openEdit}
          onPreview={setPreviewRow}
          onToggleActive={handleToggleActive}
        />
      ) : (
        <SectionCard noPadding>
          <Table<BriefingContextElement>
            rowKey="id"
            columns={columns}
            dataSource={visible}
            loading={loading}
            size="middle"
            pagination={false}
            rowClassName={(row) => (row.isActive ? '' : 'briefing-element-inactive')}
            locale={{
              emptyText: (
                <BrandedEmpty
                  icon={<ExperimentOutlined />}
                  title={hasActiveFilter ? 'No elements match' : 'No elements'}
                  description={
                    hasActiveFilter
                      ? 'Try widening the filters above, or clear them.'
                      : 'Add a new element type to make it available to briefings.'
                  }
                />
              ),
            }}
          />
        </SectionCard>
      )}

      <style>{`
        .briefing-element-inactive {
          opacity: 0.55;
        }
        .briefing-element-inactive:hover {
          opacity: 0.85;
        }
      `}</style>

      <ElementEditorDrawer
        open={editOpen}
        value={editRow}
        rendererClasses={rendererClasses}
        onClose={() => {
          setEditOpen(false);
          setEditRow(null);
        }}
        onSave={handleSave}
      />

      {previewRow && (
        <ElementPreviewDrawer
          open={!!previewRow}
          onClose={() => setPreviewRow(null)}
          element={{
            id: previewRow.id,
            code: previewRow.code,
            displayName: previewRow.displayName,
            category: previewRow.category,
            tier: previewRow.tier,
            // DB column wins; frontend fallback map covers legacy rows.
            sampleOutput: previewRow.sampleOutput ?? getSampleOutput(previewRow)?.example ?? null,
          }}
          loadPreview={previewBriefingElement}
          searchPatients={(q) => {
            const merchantId = sessionHelper.getSelectedMerchantId();
            if (!merchantId) {
              return Promise.reject(new Error('Pick a merchant from the top bar before previewing.'));
            }
            return searchMembersForPreview(merchantId, q);
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CardsView — SME default. Cards grouped by category.
// ─────────────────────────────────────────────────────────────────────────────

interface CardsViewProps {
  grouped: Array<[string, BriefingContextElement[]]>;
  loading: boolean;
  hasActiveFilter: boolean;
  onEdit: (row: BriefingContextElement) => void;
  onPreview: (row: BriefingContextElement) => void;
  onToggleActive: (row: BriefingContextElement, next: boolean) => void;
}

function CardsView({
  grouped,
  loading,
  hasActiveFilter,
  onEdit,
  onPreview,
  onToggleActive,
}: CardsViewProps) {
  if (loading && grouped.length === 0) {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
          gap: 12,
        }}
      >
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            style={{
              height: 200,
              borderRadius: 12,
              background: colors.slate[100],
              border: `1px solid ${colors.slate[200]}`,
              opacity: 0.6,
            }}
          />
        ))}
      </div>
    );
  }

  if (grouped.length === 0) {
    return (
      <SectionCard>
        <div style={{ padding: 32 }}>
          <BrandedEmpty
            icon={<ExperimentOutlined />}
            title={hasActiveFilter ? 'No elements match' : 'No elements'}
            description={
              hasActiveFilter
                ? 'Try widening the filters above, or clear them.'
                : 'Add a new element type to make it available to briefings.'
            }
          />
        </div>
      </SectionCard>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {grouped.map(([category, items]) => (
        <div key={category}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 10,
              padding: '0 4px',
            }}
          >
            <Title level={5} style={{ margin: 0, fontSize: 14 }}>
              {category}
            </Title>
            <StatusPill tone="slate">{items.length}</StatusPill>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
              gap: 12,
            }}
          >
            {items.map((el) => (
              <ElementCard
                key={el.id}
                element={el}
                onEdit={() => onEdit(el)}
                onPreview={() => onPreview(el)}
                onToggleActive={(next) => onToggleActive(el, next)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ElementCard — single element rendered for an SME.
// ─────────────────────────────────────────────────────────────────────────────

interface ElementCardProps {
  element: BriefingContextElement;
  onEdit: () => void;
  onPreview: () => void;
  onToggleActive: (next: boolean) => void;
}

function ElementCard({ element, onEdit, onPreview, onToggleActive }: ElementCardProps) {
  const [hover, setHover] = useState(false);
  const sample = getSampleOutput(element);
  const tier = TIER_COPY[element.tier] ?? { label: `T${element.tier}`, tooltip: '', color: 'default' };
  const isInactive = !element.isActive;

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        background: '#FFFFFF',
        border: `1px solid ${colors.slate[200]}`,
        borderRadius: 12,
        padding: '16px 18px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        opacity: isInactive ? 0.6 : 1,
        boxShadow: hover ? `0 6px 16px ${colors.slate[200]}66` : '0 1px 2px transparent',
        transform: hover ? 'translateY(-1px)' : 'translateY(0)',
        transition: 'box-shadow 0.15s, transform 0.15s, border-color 0.15s',
      }}
    >
      {/* Name + tier + active toggle */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            strong
            style={{ fontSize: 15, color: colors.slate[900], lineHeight: 1.3, display: 'block' }}
          >
            {element.displayName || element.code}
          </Text>
          <Tooltip title={tier.tooltip}>
            <Tag
              color={tier.color}
              style={{ marginTop: 4, marginRight: 0, fontSize: 11 }}
            >
              {tier.label}
            </Tag>
          </Tooltip>
        </div>
        <Tooltip title={element.isActive ? 'Active — included in briefings' : 'Disabled — runtime skips this element'}>
          <Switch
            size="small"
            checked={element.isActive}
            onChange={(next) => onToggleActive(next)}
          />
        </Tooltip>
      </div>

      {/* Description */}
      {element.description && (
        <Paragraph
          type="secondary"
          ellipsis={{ rows: 2 }}
          style={{ fontSize: 12.5, marginBottom: 0, lineHeight: 1.5 }}
        >
          {element.description}
        </Paragraph>
      )}

      {/* Sample output (mono, indented) */}
      <div
        style={{
          background: colors.slate[50],
          border: `1px solid ${colors.slate[200]}`,
          borderRadius: 8,
          padding: '8px 10px',
          fontFamily: typography.fontMono,
          fontSize: 11.5,
          color: colors.slate[700],
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {sample.example}
      </div>

      {/* Footer: actions */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          marginTop: 'auto',
        }}
      >
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          onClick={onPreview}
        >
          Preview
        </Button>
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          onClick={onEdit}
        >
          Edit
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ElementEditorDrawer — single drawer used for both Create and Edit
// ─────────────────────────────────────────────────────────────────────────────

interface DrawerProps {
  open: boolean;
  value: BriefingContextElement | null;
  rendererClasses: string[];
  onClose: () => void;
  onSave: (model: BriefingContextElement) => void;
}

function ElementEditorDrawer({ open, value, rendererClasses, onClose, onSave }: DrawerProps) {
  const [form] = Form.useForm<BriefingContextElement>();
  const isEdit = !!value && value.id > 0;

  useEffect(() => {
    if (open && value) {
      form.setFieldsValue(value);
    } else if (!open) {
      form.resetFields();
    }
  }, [open, value, form]);

  function handleOk() {
    form
      .validateFields()
      .then((vals) => {
        onSave({ ...(value ?? ({} as BriefingContextElement)), ...vals });
      })
      .catch(() => {});
  }

  const watchedRenderer = Form.useWatch('rendererClass', form);
  const rendererDrift =
    watchedRenderer && !rendererClasses.includes(watchedRenderer);

  return (
    <Drawer
      title={
        <Space size={8}>
          <span>{isEdit ? 'Edit element' : 'New element'}</span>
          {isEdit && value?.code && (
            <Text style={{ fontFamily: typography.fontMono, color: colors.slate[500], fontSize: 12 }}>
              {value.code}
            </Text>
          )}
        </Space>
      }
      width={520}
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={handleOk}>
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </Space>
      }
    >
      <Form<BriefingContextElement>
        form={form}
        layout="vertical"
        initialValues={value ?? undefined}
        autoComplete="off"
      >
        <Form.Item
          name="displayName"
          label="Display name"
          tooltip="The friendly name SMEs see in the catalog."
          rules={[{ max: 120 }]}
        >
          <Input placeholder="Cycle day" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          tooltip="What this element provides to the agent. Plain language, SME-readable."
          rules={[{ max: 500 }]}
        >
          <Input.TextArea
            rows={2}
            placeholder="Treatment cycle day (e.g. 'C2D8'). Drives day-of-cycle reasoning."
          />
        </Form.Item>

        <Form.Item
          name="sampleOutput"
          label="Sample output"
          tooltip="A representative one-line example of the text this element injects into the LLM prompt. SMEs see this in the Preview drawer. Leave blank to fall back to the engineering default."
          rules={[{ max: 2000 }]}
        >
          <Input.TextArea
            rows={2}
            placeholder="Active treatment protocol: FOLFOX (started 2026-04-15)."
            style={{ fontFamily: typography.fontMono, fontSize: 12.5 }}
          />
        </Form.Item>

        <Form.Item name="category" label="Category" tooltip="Used to group elements in the catalog.">
          <Select
            allowClear
            showSearch
            options={KNOWN_CATEGORIES.map((c) => ({ value: c, label: c }))}
            placeholder="Pick a category"
          />
        </Form.Item>

        <Space style={{ display: 'flex' }} size={12} align="start">
          <Form.Item
            name="tier"
            label="Refresh cadence"
            tooltip='"Stable" = cached during the conversation. "Per-turn" = refreshed every message.'
            rules={[{ required: true, type: 'number', min: 1, max: 2 }]}
            style={{ flex: 1 }}
          >
            <Select
              options={[
                { value: 1, label: 'Stable (cached per turn)' },
                { value: 2, label: 'Per-turn (refreshed)' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="defaultMaxTokens"
            label="Token budget"
            tooltip="Maximum LLM tokens this element can produce. Overridable per-briefing."
            rules={[{ required: true, type: 'number', min: 0, max: 10000 }]}
            style={{ flex: 1 }}
          >
            <InputNumber min={0} max={10000} style={{ width: '100%' }} />
          </Form.Item>
        </Space>

        <Form.Item
          name="isActive"
          label="Active"
          tooltip="Kill switch. Disabled elements are skipped at render time."
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        {/* ── Advanced — engineering fields ─────────────────────────────── */}
        <div
          style={{
            marginTop: 18,
            paddingTop: 16,
            borderTop: `1px dashed ${colors.slate[200]}`,
          }}
        >
          <Text
            strong
            style={{
              display: 'block',
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: 0.8,
              color: colors.slate[500],
              marginBottom: 10,
            }}
          >
            Advanced
          </Text>

          <Form.Item
            name="code"
            label="Code"
            tooltip="Stable engineering identifier. Lowercase, dot-separated. Immutable after create — audit rows reference it."
            rules={[
              { required: true, message: 'Code is required' },
              {
                pattern: /^[a-z0-9._-]+$/,
                message: 'Lowercase letters, digits, dots, dashes, underscores only',
              },
              { max: 64 },
            ]}
          >
            <Input
              placeholder="state.cycle-day"
              disabled={isEdit}
              style={{ fontFamily: typography.fontMono }}
            />
          </Form.Item>

          <Form.Item
            name="rendererClass"
            label="Renderer class"
            tooltip="C# class that produces the actual text. Pick from the live DI registry."
          >
            <Select
              allowClear
              showSearch
              options={rendererClasses.map((c) => ({ value: c, label: c }))}
              placeholder="Pick a renderer"
              popupRender={(menu) => (
                <>
                  {menu}
                  {rendererDrift && (
                    <div
                      style={{
                        borderTop: `1px solid ${colors.amber[500]}33`,
                        padding: '6px 10px',
                        background: colors.amber[100],
                        fontSize: 12,
                        color: colors.amber[700],
                      }}
                    >
                      <WarningOutlined /> &quot;{watchedRenderer}&quot; isn&apos;t in DI right now.
                    </div>
                  )}
                </>
              )}
            />
          </Form.Item>

          <Form.Item
            name="sortOrder"
            label="Sort order"
            tooltip="Display ordering hint inside the catalog."
            rules={[{ type: 'number' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </div>
      </Form>
    </Drawer>
  );
}
